/**
 * @fileoverview Input - Accessible form input component
 *
 * Purpose: Provides styled, accessible input field with focus states
 * Dependencies: None (native HTML input extended)
 * Used by: All forms requiring text input fields
 */

import * as React from "react";

import { cn } from "@/utils/misc";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {}

/**
 * Input component with consistent styling and accessibility
 *
 * Features:
 * - Accessible focus states with ring indicators
 * - Disabled state styling
 * - File input styling
 * - Placeholder styling
 * - Extends all native input HTML attributes
 *
 * @component
 * @example
 * <Input type="text" placeholder="Enter your name" />
 * <Input type="email" required />
 * <Input type="password" disabled />
 * <Input type="file" />
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
          "file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Input.displayName = "Input";

export { Input };
